/* Map's three pure helpers, the way kinds.ts holds Index's two.

   Moved out of `web/src/workspace/g-map/` in Phase 4 of the native client —
   `byDistance` and `centreMoved` from `g-map.ts`, `localityOf` from its
   `kinds.ts` — rather than copied, for the reason README.md gives and ADRs 0003
   and 0011 each paid for once. The drift here would be the invisible kind: a
   sort order or an address-segment index one character out of step would put
   the same 505 venues in a different order on the two clients, and nothing
   would fail.

   What did NOT move is `g-map/kinds.ts`'s icon and hue table. That one is
   already two implementations by an earlier decision, because each client
   states its colours in its own token vocabulary — `var(--k-food)` against a
   `keyof KindColors`. There is nothing framework-free left in it to share. */

/** Enough of a venue to order by distance. Structural rather than the api-types
 *  `Venue`, because this package has no dependencies (README.md rule 2) — and
 *  `byDistance` is generic, so a caller still gets its own row type back. */
export interface Nearable {
  name: string;
  lat: number | null;
  lng: number | null;
  geocoded: boolean;
}

/** Where the map is looking. */
export interface Centre {
  lat: number;
  lng: number;
}

/**
 * Order venues by how far they are from a point, nearest first.
 *
 * Equirectangular rather than haversine, with the centre's cosine hoisted out
 * of the loop and compared on d² with no `sqrt`: four float operations per
 * venue. Over ~505 venues that is less work than the `localeCompare` sort the
 * desktop dock already runs on every camera settle — **ordering is not what
 * costs anything on this screen** (ADR 0012 decision 5, which rejected freezing
 * the order on cost grounds specifically).
 *
 * Keys are computed once into a decorated array rather than inside the
 * comparator, which would evaluate them ~4,700 times instead of 505.
 *
 * Venues with no coordinates sort last and alphabetically among themselves —
 * the row already labels them `off-map` (ADR 0012 §5). With no centre the whole
 * list is alphabetical, which is the cold-start answer before the camera has
 * reported anything.
 */
export function byDistance<T extends Nearable>(list: readonly T[], centre: Centre | null): T[] {
  if (!centre) return [...list].sort((a, b) => a.name.localeCompare(b.name));

  const k = Math.cos((centre.lat * Math.PI) / 180);
  const keyed = list.map((v) => {
    if (!v.geocoded || v.lat == null || v.lng == null) return { v, d: Infinity };
    /* Shortest way round, so a venue just over the antimeridian is near rather
       than 359 degrees away. */
    let dLng = v.lng - centre.lng;
    if (dLng > 180) dLng -= 360;
    else if (dLng < -180) dLng += 360;
    const dx = dLng * k;
    const dy = v.lat - centre.lat;
    return { v, d: dx * dx + dy * dy };
  });

  keyed.sort((a, b) => a.d - b.d || a.v.name.localeCompare(b.v.name));
  return keyed.map((x) => x.v);
}

/**
 * Whether the camera has moved enough to deserve a fresh order.
 *
 * ADR 0017 §8 reduces the whole snapshot question to "a single comparison at
 * entry", and this is it. Re-ordering only when the centre actually changed is
 * what keeps leaving and coming back lossless: an unchanged answer means the
 * caller hands the list the same array identity, and the scroller keeps its own
 * offset rather than having one restored to it.
 *
 * The epsilon is deliberate. A camera centre arrives as a float from the maps
 * SDK, so an exact `!==` would report a move on a pan that ended where it
 * started.
 */
export function centreMoved(from: Centre | null, to: Centre | null): boolean {
  if (!from || !to) return Boolean(from) !== Boolean(to);
  return Math.abs(to.lat - from.lat) > 1e-9 || Math.abs(to.lng - from.lng) > 1e-9;
}

/**
 * A venue's short locality label — city if the geocoder gave us one, else the
 * locality-ish segment of the formatted address.
 *
 * Index 1, not 0: a Google formatted address leads with the street line
 * ("Jl. Raya Ubud No.88, Ubud, Bali"), so the second segment is the town.
 * Ported from v1's `placeCity`.
 */
export function localityOf(p: { city: string | null; formatted_address: string }): string {
  if (p.city) return p.city;
  const parts = (p.formatted_address || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  return parts.length > 1 ? parts[1] : parts[0];
}
